// src/pages/HistoryTimeline.jsx
import React, { useMemo, useRef, useEffect, useState } from "react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

/* Image imports — replace or add these files in src/assets/ if you want images inline */
import img1941 from "../../assets/history-1941.avif";
import img1942 from "../../assets/history-1942.avif";
import img1948 from "../../assets/history-1948.jpg";
import img1963 from "../../assets/history-1963.jpg";
import img1966 from "../../assets/history-1966.jpg";
import img1970 from "../../assets/history-1970.jpg";
import img2023 from "../../assets/c2.jpeg";

const timelineData = [
  {
    year: 1941,
    title: "Decree, Consecration & Public Opening",
    excerpt:
      "St. Stephen's began as a private residence for Catholic worship. A decree on March 12, 1941, established plans for the new parish, and the residence was consecrated and opened to the public on September 5, 1941.",
    details:
      "Like many early churches, St. Stephen's Church began not as a purpose-built structure but as a private residence where Catholics gathered to pray and celebrate the Holy Eucharist. The location, a bungalow acquired from Keshavdeo Ghanshamdas Poddar, became the first site of the church. Archbishop Roberts' decree of March 12, 1941, officially defined the parish boundaries for the new St. Stephen's Church at Cumballa Hill. Later that year, on September 5, 1941, the converted residence was consecrated and opened to the public, establishing the foundation of the parish.",
    image: img1941,
  },
  {
    year: 1942,
    title: "Acquisition of Property Finalized",
    excerpt:
      "On January 21, 1942, the church finalized the purchase of the bungalow property for ₹1,75,000, aided by a loan from Bhuleshwar Cathedral.",
    details:
      "The plot of land on which St. Stephen's stands was officially purchased on January 21, 1942, for ₹1,75,000. The acquisition, facilitated by a loan from the Bhuleshwar Cathedral (then the Cathedral of the Archdiocese of Bombay), ensured the church had permanent ownership of its site. This gave the parish a stable foundation for future expansion and development.",
    image: img1942,
  },
  {
    year: 1948,
    title: "Official Erection as a Parish",
    excerpt:
      "On December 26, 1948, the church was officially blessed and formally declared as a parish by Archbishop Roberts.",
    details:
      "Seven years after its consecration, St. Stephen's was officially established as a parish on December 26, 1948 — the Feast of St. Stephen. The Eucharistic celebration marked the blessing and formal declaration of the parish, with Rev. J. A. Lobo serving as Vicar. The official Decree of Erection was read in Latin, recognizing the growing Catholic population in the Cumballa Hill area and the community's need for a dedicated parish identity.",
    image: img1948,
  },
  {
    year: 1963,
    title: "Construction of the New Church Begins",
    excerpt:
      "As the parish grew, construction for a new, larger church building began in November 1963 under Fr. Frank E. Lobo.",
    details:
      "By the early 1960s, the small converted residence could no longer accommodate the expanding congregation. Under the leadership of Fr. Frank E. Lobo, plans were set in motion to construct a purpose-built church. The long-awaited rebuilding project began in November 1963, marking a major step in the modernization and expansion of St. Stephen's Parish.",
    image: img1963,
  },
  {
    year: 1966,
    title: "Opening of the New Church Building",
    excerpt:
      "The newly constructed St. Stephen's Church building opened on September 18, 1966 — exactly 25 years after the original consecration.",
    details:
      "The new St. Stephen's Church was inaugurated and opened to the faithful on September 18, 1966. The event marked the 25th anniversary of the church's original establishment. The modern structure represented the growth of the community and provided a much larger and architecturally advanced worship space than before.",
    image: img1966,
  },
  {
    year: 1970,
    title: "Architectural Completion & Design Legacy",
    excerpt:
      "Architect Perin J. Mistri's modern design, influenced by Art Deco and Le Corbusier styles, was completed through the 1970s.",
    details:
      "Architect Perin J. Mistri, India's first qualified woman architect, designed the new church with a blend of Art Deco and Le Corbusier influences. Her design featured a striking white concave tower with a crucifix within its recess, deep-set windows, and thick walls to maintain cool interiors suited for Mumbai's climate. The construction extended into the 1970s due to height approval delays. The final result remains an architectural landmark — practical, elegant, and enduringly symbolic of the parish's evolution.",
    image: img1970,
  },
  {
    year: 2023,
    title: "75th Anniversary Celebrations",
    excerpt:
      "In 2023, St. Stephen's joyfully celebrated its 75th year — a milestone anniversary for the parish community.",
    details:
      "2023 marked the 75th anniversary of St. Stephen's Church — a significant milestone of faith and service. The parish marked the occasion with heartfelt celebrations, thanksgiving services, and community gatherings that honoured seven and a half decades of worship, outreach, and fellowship. Parishioners, both long-standing and recent, reflected on the generations of ministry and the continuing commitment to serving the neighbourhood. Special liturgies and commemorative events brought the community together in gratitude and remembrance.",
    image: img2023,
  },
];

function useRevealOnScroll(selector = ".reveal") {
  useEffect(() => {
    const els = Array.from(document.querySelectorAll(selector));
    if (!("IntersectionObserver" in window) || els.length === 0) {
      els.forEach((el) => el.classList.add("revealed"));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("revealed");
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: "0px 0px -12% 0px", threshold: 0.06 }
    );

    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [selector]);
}

export default function HistoryTimeline() {
  const sortedData = useMemo(() => {
    return [...timelineData].sort((a, b) => a.year - b.year);
  }, []);

  const [activeYear, setActiveYear] = useState(null);
  const [isDockScrolled, setIsDockScrolled] = useState(false);
  const [dockVisible, setDockVisible] = useState(true);
  const [exporting, setExporting] = useState(false);
  const groupedYears = useMemo(
    () => [...new Set(sortedData.map((s) => s.year))].sort((a, b) => a - b),
    [sortedData]
  );

  const timersRef = useRef([]);

  useRevealOnScroll(".reveal");

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const docHeight = document.documentElement.scrollHeight;
      const winHeight = window.innerHeight;
      const scrollPercent = (scrollY / (docHeight - winHeight)) * 100;
      
      setIsDockScrolled(scrollY > 100);
      // Hide dock after 50% of page is scrolled
      setDockVisible(scrollPercent < 50);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      timersRef.current.forEach((t) => clearTimeout(t));
      timersRef.current = [];
    };
  }, []);

  const scrollToYear = (year) => {
    setActiveYear(year);
    const el = document.getElementById(`year-${year}`);
    if (el) {
      // Account for navbar (135px) + dock bar (~60px) + gap
      const offset = 135 + 70 + 20;
      const top = el.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  const exportTimelinePDF = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const sections = groupedYears
        .map((y) => document.getElementById(`year-${y}`))
        .filter(Boolean);

      if (!sections.length) {
        alert("Nothing to export.");
        setExporting(false);
        return;
      }

      const pdf = new jsPDF({ unit: "pt", format: "a4", orientation: "portrait" });
      const a4Width = pdf.internal.pageSize.getWidth();
      const a4Height = pdf.internal.pageSize.getHeight();

      for (let i = 0; i < sections.length; i++) {
        const el = sections[i];
        const origBg = el.style.background;
        el.style.background = "#ffffff";

        const scale = 4;
        const canvas = await html2canvas(el, { scale, useCORS: true, backgroundColor: null, logging: false });

        el.style.background = origBg;

        const imgData = canvas.toDataURL("image/jpeg", 0.95);
        const imgW = (canvas.width / scale) * 0.75;
        const imgH = (canvas.height / scale) * 0.75;

        const ratio = Math.min(a4Width / imgW, 1);
        const renderW = imgW * ratio;
        const renderH = imgH * ratio;
        const x = (a4Width - renderW) / 2;
        const y = 18;

        if (i > 0) pdf.addPage();
        pdf.addImage(imgData, "JPEG", x, y, renderW, renderH, undefined, "FAST");
      }

      pdf.save("st-stephens-timeline.pdf");
    } catch (err) {
      console.error("Export failed:", err);
      alert("Export failed — check console for details.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <main className="min-h-screen bg-gradient-to-b from-white to-amber-50/30">
      <style>{`
        :root {
          --container-max: 1600px;
          --side-gap: 40px;
          --navbar-height: 135px;
        }

        /* Reveal animation */
        .reveal {
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.8s cubic-bezier(0.2, 0.9, 0.2, 1);
        }
        .reveal.revealed {
          opacity: 1;
          transform: translateY(0);
        }

        /* Timeline container */
        .timeline-container {
          position: relative;
          max-width: var(--container-max);
          margin: 0 auto;
          padding: 0 var(--side-gap);
        }

        /* center line */
        .timeline-line {
          position: absolute;
          left: 50%;
          top: 0;
          bottom: 0;
          width: 4px;
          background: linear-gradient(to bottom, #fbbf24, #f59e0b, #d97706);
          transform: translateX(-50%);
          z-index: 1;
        }

        .timeline-item {
          position: relative;
          margin-bottom: 80px;
          z-index: 2;
        }

        .timeline-content {
          width: calc(50% - 40px);
          position: relative;
        }
        .timeline-content.left {
          margin-left: 0;
          margin-right: auto;
          padding-right: 80px;
        }
        .timeline-content.right {
          margin-left: auto;
          margin-right: 0;
          padding-left: 80px;
        }

        .timeline-dot {
          position: absolute;
          left: 50%;
          top: 30px;
          transform: translateX(-50%);
          width: 24px;
          height: 24px;
          background: #fff;
          border: 5px solid #f59e0b;
          border-radius: 50%;
          z-index: 10;
          box-shadow: 0 0 0 10px rgba(251, 191, 36, 0.15);
          transition: all 0.3s ease;
        }

        .timeline-item:hover .timeline-dot {
          transform: translateX(-50%) scale(1.3);
          box-shadow: 0 0 0 15px rgba(251, 191, 36, 0.25);
        }

        .timeline-card {
          background: white;
          border-radius: 16px;
          padding: 32px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          border: 2px solid rgba(251, 191, 36, 0.2);
          position: relative;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          overflow: visible;
          width: 100%;
        }

        .timeline-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 12px 40px rgba(251, 191, 36, 0.3);
          border-color: #fbbf24;
        }

        .timeline-card::before {
          content: '';
          position: absolute;
          top: 40px;
          width: 60px;
          height: 3px;
          background: linear-gradient(90deg, #fbbf24, #f59e0b);
        }
        .timeline-content.left .timeline-card::before {
          right: -60px;
        }
        .timeline-content.right .timeline-card::before {
          left: -60px;
        }

        .timeline-card .details {
          max-height: 0;
          overflow: hidden;
          padding-top: 0;
          transition: max-height 420ms cubic-bezier(0.2,0.9,0.2,1), padding 320ms ease;
        }
        .timeline-card .details.expanded {
          max-height: 1000px;
          padding-top: 1rem;
        }

        nav .year-btn {
          position: relative;
          border-radius: 9999px;
          padding: 0.5rem 1.25rem;
          font-weight: 700;
          font-size: 0.9rem;
          color: #1f2937;
          background: transparent;
          border: none;
          cursor: pointer;
          transition: all 0.3s ease;
          white-space: nowrap;
          flex-shrink: 0;
        }
        nav .year-btn::after {
          content: "";
          position: absolute;
          left: 50%;
          bottom: 6px;
          transform: translateX(-50%);
          width: 0;
          height: 3px;
          border-radius: 999px;
          background: linear-gradient(90deg, #fbbf24, #f59e0b);
          opacity: 0;
          transition: width 240ms ease, opacity 240ms ease;
        }
        nav .year-btn:hover { color: #b45309; background: rgba(255, 255, 255, 0.2); }
        nav .year-btn:hover::after { width: 60%; opacity: 0.6; }
        nav .year-btn.active { color: #b45309; }
        nav .year-btn.active::after { width: 70%; opacity: 1; }

        /* Mobile Optimizations */
        @media (max-width: 1200px) {
          .timeline-content { width: calc(50% - 30px); }
          .timeline-content.left { padding-right: 60px; }
          .timeline-content.right { padding-left: 60px; }
        }

        @media (max-width: 768px) {
          :root {
            --side-gap: 0px;
          }

          .timeline-line { left: 6px; }
          .timeline-dot { left: 6px; }
          .timeline-content { 
            width: calc(100% - 16px) !important; 
            margin-left: 16px !important; 
            padding-left: 0 !important; 
            padding-right: 0 !important; 
          }
          .timeline-card::before { display: none; }
          .timeline-card { padding: 12px; }
          
          /* Year navigation responsive */
          nav .year-btn {
            padding: 0.3rem 0.6rem;
            font-size: 0.7rem;
          }
          
          /* Prevent text overflow */
          .timeline-card h3 {
            font-size: 1.1rem;
            word-wrap: break-word;
            overflow-wrap: break-word;
          }
          
          .timeline-card p {
            font-size: 0.875rem;
            word-wrap: break-word;
            overflow-wrap: break-word;
          }
        }

        @media (max-width: 480px) {
          :root {
            --side-gap: 0px;
          }

          .timeline-line { left: 4px; }
          .timeline-dot { 
            left: 4px;
            width: 14px;
            height: 14px;
            border-width: 3px;
          }
          .timeline-content { 
            width: calc(100% - 14px) !important; 
            margin-left: 14px !important; 
          }
          
          nav .year-btn {
            padding: 0.25rem 0.5rem;
            font-size: 0.65rem;
            font-weight: 600;
          }
          
          .timeline-card {
            padding: 8px;
          }
          
          .timeline-card h3 {
            font-size: 1rem;
          }
        }

        @media (max-width: 390px) {
          :root {
            --side-gap: 0px;
          }

          .timeline-line { left: 3px; }
          .timeline-dot { 
            left: 3px;
            width: 12px;
            height: 12px;
            border-width: 2px;
          }
          .timeline-content { 
            width: calc(100% - 12px) !important; 
            margin-left: 12px !important; 
          }
          
          nav .year-btn {
            padding: 0.2rem 0.4rem;
            font-size: 0.6rem;
          }

          .timeline-card {
            padding: 10px;
          }
        }
      `}</style>

      {/* Header */}
      <header className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16 text-center">
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight mb-4 bg-gradient-to-r from-amber-600 to-amber-800 bg-clip-text text-transparent">
          Our History & Timeline
        </h1>
        <p className="text-gray-600 text-base sm:text-lg max-w-3xl mx-auto mb-6 px-4">
          Journey through the milestones that shaped our parish. Click any event to learn more.
        </p>
      </header>

      {/* Year Navigation - Fixed below navbar */}
      <div
        className="sticky"
        style={{
          top: "var(--navbar-height, 135px)",
          zIndex: 50,
          opacity: dockVisible ? 1 : 0,
          pointerEvents: dockVisible ? "auto" : "none",
          transform: dockVisible ? "translateY(0)" : "translateY(-20px)",
          transition: "opacity 0.5s ease, transform 0.5s ease",
        }}
      >
        <nav aria-label="Year navigation" className="max-w-max mx-auto transition-all duration-500 ease-in-out px-2 sm:px-4">
          <ul
            className="flex gap-0.5 sm:gap-1 md:gap-2 items-center justify-center rounded-full transition-all duration-500 ease-in-out overflow-x-auto"
            style={{
              background: "rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(40px) saturate(200%)",
              WebkitBackdropFilter: "blur(40px) saturate(200%)",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12), 0 4px 12px rgba(251, 191, 36, 0.15)",
              border: "1px solid rgba(255, 255, 255, 0.4)",
              padding: isDockScrolled ? "0.4rem 0.6rem" : "0.6rem 1rem",
              transform: isDockScrolled ? "scale(0.95)" : "scale(1)",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
          >
            {groupedYears.map((year) => (
              <li key={year}>
                <button
                  onClick={() => scrollToYear(year)}
                  className={`year-btn focus:outline-none focus:ring-2 focus:ring-amber-300 ${activeYear === year ? "active" : ""}`}
                  style={{
                    fontSize: isDockScrolled ? "0.65rem" : "0.75rem",
                    padding: isDockScrolled ? "0.3rem 0.6rem" : "0.4rem 0.8rem",
                  }}
                >
                  {year}
                </button>
              </li>
            ))}
          </ul>
        </nav>
      </div>

      {/* Timeline */}
      <section className="px-2 sm:px-6 py-12 sm:py-20">
        <div className="timeline-container">
          <div className="timeline-line" />

          {sortedData.map((item, idx) => {
            const isLeft = idx % 2 === 0;
            return (
              <div key={idx} id={`year-${item.year}`} className="timeline-item reveal">
                <div className="timeline-dot" />
                <div className={`timeline-content ${isLeft ? "left" : "right"}`}>
                  <TimelineCard item={item} />
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Export PDF Button at Bottom */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16 text-center">
        <div className="flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={exportTimelinePDF}
            className="px-6 py-3 rounded-lg bg-gray-900 text-white font-semibold text-sm sm:text-base hover:bg-gray-800 transition-colors duration-200"
            disabled={exporting}
            aria-busy={exporting}
          >
            {exporting ? "Exporting..." : "Export Timeline as PDF"}
          </button>
        </div>
      </section>
    </main>
  );
}

function TimelineCard({ item }) {
  const [open, setOpen] = useState(false);
  const detailsId = `details-${item.year}`;
  const btnRef = useRef(null);

  const closedBtnStyle = {
    background: "linear-gradient(90deg,#f59e0b,#f97316)",
    color: "#fff",
    border: "none",
  };

  const openBtnStyle = {
    background: "#fff",
    color: "#b45309",
    border: "1px solid rgba(245,158,11,0.12)",
  };

  const toggle = () => {
    setOpen((v) => !v);
    const el = btnRef.current;
    if (el) {
      el.style.transform = "translateZ(0)";
      void el.offsetHeight;
      requestAnimationFrame(() => (el.style.transform = "translateZ(0)"));
    }
  };

  return (
    <article className="timeline-card" style={{ position: "relative" }}>
      {/* Year badge */}
      <div className="inline-flex items-center gap-2 mb-3 px-3 sm:px-4 py-1.5 bg-gradient-to-r from-amber-400 to-amber-600 text-white rounded-full text-xs sm:text-sm font-bold shadow-md">
        <svg className="w-3 h-3 sm:w-4 sm:h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
        </svg>
        <span>{item.year}</span>
      </div>

      <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 leading-tight text-center md:text-left">
        {item.title}
      </h3>

      <p className="text-gray-600 text-sm leading-relaxed mb-4 text-center md:text-left">
        {item.excerpt}
      </p>

      {!open && (
        <button
          ref={btnRef}
          onClick={toggle}
          aria-expanded={open}
          aria-controls={detailsId}
          className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold transition-all focus:outline-none focus:ring-2 focus:ring-amber-300 shadow-md hover:shadow-lg"
          style={{
            ...closedBtnStyle,
            transform: "translateZ(0)",
            willChange: "transform, opacity",
            position: "relative",
            zIndex: 2,
          }}
        >
          <span style={{ display: "inline-block", lineHeight: 1 }}>Read more</span>
          <svg className="w-4 h-4" viewBox="0 0 20 20" fill="none" aria-hidden style={{ stroke: "currentColor" }}>
            <path d="M5 8l5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}

      <div
        id={detailsId}
        className={`details ${open ? "expanded" : ""}`}
        aria-hidden={!open}
        style={{
          transition: "max-height 420ms cubic-bezier(0.2,0.9,0.2,1), padding 320ms ease",
          maxHeight: open ? "1000px" : "0px",
          overflow: "hidden",
          paddingTop: open ? "1rem" : "0",
        }}
      >
        <div className="mt-4 pt-4 border-t border-amber-100">
          <p className="text-gray-700 text-sm sm:text-base leading-relaxed text-center md:text-left">{item.details}</p>
        </div>
      </div>

      {open && (
        <div style={{ marginTop: 12 }}>
          <button
            onClick={toggle}
            aria-expanded={open}
            aria-controls={detailsId}
            className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold transition-all focus:outline-none focus:ring-2 focus:ring-amber-300 shadow-sm"
            style={{
              ...openBtnStyle,
              transform: "translateZ(0)",
              willChange: "transform, opacity",
              position: "relative",
              zIndex: 2,
            }}
          >
            <span style={{ display: "inline-block", lineHeight: 1 }}>Show less</span>
          </button>
        </div>
      )}

      {/* IMAGE */}
      {item.image && (
        <div
          className="mt-4 sm:mt-6"
          style={{
            transition: "max-height 520ms cubic-bezier(0.2,0.9,0.2,1), opacity 420ms ease",
            maxHeight: open ? 700 : 340,
            overflow: "hidden",
          }}
        >
          <img
            src={item.image}
            alt={item.title}
            style={{
              width: "100%",
              height: "auto",
              maxHeight: "700px",
              objectFit: "contain",
              display: "block",
              borderRadius: 12,
              boxShadow: "0 8px 30px rgba(16,24,40,0.06)",
            }}
          />
        </div>
      )}
    </article>
  );
}